<template>
  <div
    class="one-section-menu"
    :data-one-component="$options.name"
    v-bind="props.root"
    :style="menuStyle"
  >
    <OneContainer v-bind="props.container" :override="props.container">
      <!-- left -->
      <div>
        <div class="flex flex-row items-center">
          <!-- back button -->
          <OneButton
            type="icon"
            size="auto"
            v-bind="props.backButton"
            icon="ArrowLeft"
            v-if="showBackButton"
            @update:click="onBackBtnClick()"
          />
          <!-- logo -->
          <div class="flex h-16 w-16 shrink-0">
            <OneImage
              class="logo"
              v-bind="props.logo"
              v-if="props.logo && props.logo.src"
              @click="onLogoClick()"
            />
          </div>

          <OneLink v-bind="title.link" v-if="title">
            <OneHeading class="pl-4 !text-lg" :content="title.title" :level="2" />
          </OneLink>
        </div>
      </div>

      <!-- center -->
      <div></div>

      <!-- right -->
      <div class="w-full justify-end flex flex-row items-center">
        <!-- commerce: cart button -->
        <OneButton
          class="mr-2"
          type="icon"
          size="auto"
          v-bind="props.cartButton"
          :icon="cartButtonIcon"
          v-if="cartItemsCount > 0"
          @update:click="onCartBtnClick()"
          :key="`icon-${cartButtonIcon}`"
        />

        <!-- commerce: user button -->
        <OneButton
          class="mr-2 children:w-7"
          type="icon"
          size="auto"
          v-bind="props.userButton"
          icon="User"
          @update:click="onUserBtnClick()"
          v-if="showUserButton"
          :key="`icon-User`"
        />

        <!-- hamburger -->
        <div v-bind="props.hamburgerWrap" v-if="showHamburger">
          <!-- hamburger: closed menu -->
          <OneButton
            type="icon"
            size="auto"
            icon="Hamburger1"
            key="Hamburger1"
            v-if="showHamburger && !menuOpen"
            @update:click="menuOpen = true"
          />
          <!-- hamburger: opened menu -->
          <OneButton
            type="icon"
            size="auto"
            icon="Hamburger1Close"
            key="Hamburger1Close"
            v-if="showHamburger && menuOpen"
            @update:click="menuOpen = false"
          />
        </div>
      </div>

      <!-- menu items -->
      <div class="menu-items" v-bind="props.menuItems" v-if="menuOpen">
        <OneLink
          v-bind="{...props.menuItem, ...item}"
          v-for="(item, index) in menuItems"
          :key="`item-${index}`"
          >{{ item.title }}</OneLink
        >

        <span class="lang-separator" v-if="siteLangsFull && siteLangsFull.length > 0"></span>

        <!-- lang switch -->
        <OneLink
          v-bind="{...props.menuLang, path: `/${lang.code}`, switchLangTo: lang.code}"
          v-for="(lang, index) in siteLangsFull"
          :key="`lang-${index}`"
          >{{ lang.title }}</OneLink
        >

        <!-- currency switch -->
        <div class="currency p-3" v-if="shopCurrenciesEnabledCodes">
          <div v-for="(currency, index) in shopCurrenciesEnabledCodes" :key="index">
            <span
              @click="setCartCurrency(currency)"
              class="cursor-pointer"
              :class="{'!font-bold': currency == cartCurrency}"
              >{{ currency }}</span
            >
          </div>
        </div>
      </div>
    </OneContainer>
  </div>
</template>

<script>
import mixinClasses from "../../../mixins/classes"

export default {
  name: "OneMenu2",
  mixins: [mixinClasses],
  props: {
    title: {
      type: Object,
    },
    openedSize: {
      type: String,
      default: "small",
    },
    fixed: {
      type: Boolean,
      default: false,
    },
    autohide: {
      type: Boolean,
      default: true,
    },
    showHamburgerButton: {
      type: Boolean,
      default: true,
    },
    showBackButton: {
      type: Boolean,
      default: false,
    },
    showCartButton: {
      type: Boolean,
      default: false,
    },
    showUserButton: {
      type: Boolean,
      default: false,
    },
    cartItemsCount: {
      type: Number,
      default: null,
    },
    cartCurrency: {
      type: String,
    },
    shopCurrenciesEnabledCodes: {
      type: Array,
    },

    // positions
    hamburgerWrapPosition: {
      type: String,
      default: "right",
    },
    cartButtonIcon: {
      type: String,
      default: "Cart1",
    },
    cartButtonPosition: {
      type: String,
      default: "right",
    },
    logoPosition: {
      type: String,
      default: "center",
    },
    backButtonPosition: {
      type: String,
      default: "left",
    },

    keepLogoCentered: {
      type: Boolean,
      default: true,
    },
    menuItems: {
      type: Array,
    },
    background: {
      type: String,
      default: "transparent",
    },
    currentLang: {
      type: String,
    },
    siteLangs: {
      type: Array,
      default: () => ["cs", "en"],
    },
  },
  emits: [
    "update:logoClick",
    "update:backBtnClick",
    "update:cartBtnClick",
    "update:setCartCurrency",
  ],
  data: () => ({
    prevScrollPos: 0,
    menuStyle: null,
    menuOpen: false,
    menuVisible: true,
    defaults: {
      commonClasses: "",
      root: {
        class: "w-full transition-all duration-500 ease-in-out",
        propClass: {
          fixed: {
            true: "fixed top-0 z-50",
            false: "relative z-0",
          },
          background: {
            white: "bg-white",
            transparent: "",
          },
        },
      },
      container: {
        class: "grid grid-cols-3 grid-rows-1",
        contentLayout: "grid",
        contentAlign: "none",
      },
      langWrap: {
        class: "col-span-1 row-start-1 row-span-1 self-center",
        propClass: {},
      },
      hamburgerWrap: {
        class: "flex flex-row justify-center items-center col-span-1 row-start-1 row-span-1 z-20",
        propClass: {
          hamburgerWrapPosition: {
            left: "col-start-1 justify-self-start",
            center: "col-start-2 justify-self-center",
            right: "col-start-3 justify-self-end",
          },
        },
      },
      backButton: {
        class: "col-span-1 row-start-1 row-span-1",
        propClass: {
          backButtonPosition: {
            left: "col-start-1 justify-self-start",
            center: "col-start-2 justify-self-center",
            right: "col-start-3 justify-self-end",
          },
        },
      },
      logo: {
        class: "cursor-pointer col-span-1 row-start-1 row-span-1 flex-center",
        propClass: {
          logoPosition: {
            left: "col-start-1 justify-self-start",
            center: "col-start-2 justify-self-center",
            right: "col-start-3 justify-self-end",
          },
        },
        fit: "contain",
        style: {
          maxWidth: "200px",
          maxHeight: "30px",
          alignSelf: "center",
        },
      },
      cartButton: {
        class: "col-span-1 row-start-1 row-span-1",
        propClass: {
          cartButtonPosition: {
            left: "col-start-1 justify-self-start",
            center: "col-start-2 justify-self-center",
            right: "col-start-3 justify-self-end",
          },
        },
      },
      menuItems: {
        class: "",
        propClass: {
          openedSize: {
            small: "absolute top-0 flex flex-col mt-20 px-2 xs:px-4 sm:px-6 xl:px-32 pb-5",
            fullscreen:
              "absolute top-0 w-screen h-screen z-10 flex flex-col justify-center items-center text-center ",
          },
          hamburgerWrapPosition: {
            // this is intentinal - to align the menu items with the hamburger icon
            left: "left-0 text-left",
            center: "left-0 text-center w-full",
            right: "right-0 text-right",
          },
          background: {
            white: "bg-white",
            transparent: "",
          },
        },
      },
      menuItem: {
        class: "px-3 my-1 py-1",
        propClass: {
          openedSize: {
            small: "",
            fullscreen: "!w-auto",
          },
        },
      },
      menuLang: {
        class: "px-3 my-1 py-1 text-dark",
      },
    },
  }),
  computed: {
    siteLangsFull() {
      let langLength = "long"
      let l = []
      let langsMap = {
        cs: {
          short: "cs",
          long: "česky",
        },
        en: {
          short: "en",
          long: "english",
        },
      }

      this.siteLangs.forEach((lang) => {
        if (lang != this.currentLang) {
          l.push({
            title: langsMap[lang][langLength],
            code: lang,
          })
        }
      })

      return l
    },
    showHamburger() {
      return (this.showHamburgerButton && this.menuItems.length > 0) || this.siteLangs.length > 1
    },
    // showUserButton() {
    //   return this.showUserButton
    // },
    showBackBtnFill() {
      // return true
      return this.keepLogoCentered && !this.showBackButton && !this.showHamburger
    },
    showCartBtnFill() {
      return this.keepLogoCentered && !this.showCartButton && this.showHamburger
    },
  },
  created() {
    if (this.autohide) this.enableAutohide()
  },
  methods: {
    enableAutohide() {
      window.addEventListener("scroll", (e) => {
        this.scrolled(e)
      })
    },
    scrolled() {
      let currentScrollPos = window.pageYOffset
      // console.log('scrolled:', currentScrollPos, this.prevScrollPos, e)
      if (this.prevScrollPos >= currentScrollPos) {
        if (!this.menuVisible) this.showMenu()
      } else {
        if (this.menuVisible && this.prevScrollPos >= 0) this.hideMenu()
      }
      this.prevScrollPos = currentScrollPos
    },
    showMenu() {
      // console.log('showMenu()')
      this.menuStyle = {
        top: "0",
      }
      this.menuVisible = true
    },
    hideMenu() {
      // console.log('hideMenu()')
      this.menuStyle = {
        top: "-100px",
      }
      this.menuVisible = false
      this.menuOpen = false
    },
    onLogoClick() {
      this.$emit("update:logoClick")
    },
    onBackBtnClick() {
      this.$emit("update:backBtnClick")
    },
    onCartBtnClick() {
      this.$emit("update:cartBtnClick")
    },
    onUserBtnClick() {
      this.$emit("update:userBtnClick")
    },
    setCartCurrency(v) {
      this.$emit("update:setCartCurrency", v)
    },
  },
}
</script>

<style scope lang="less">
.btn-back-fill,
.btn-cart-fill {
  width: 48px;
  height: 48px;
}
</style>